import React, { useEffect, useRef, useState, useCallback } from 'react'

import { Text, Row, Box, Button } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import MagentaStar from './magenta-star'
import styles from './styles.module.scss'

interface Props {
    name: string
    title: string
    numStars: number
    reviewText: string
}

const ReviewCard: React.FC<Props> = ({ name, title, numStars, reviewText }) => {
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    const reviewTextRef = useRef<HTMLParagraphElement>(null)
    const innerWrapperRef = useRef<HTMLDivElement>(null)
    const innerWrapperHeight = useRef<number>(0)
    const upperContentRef = useRef<HTMLDivElement>(null)

    function isOverflown(element: HTMLElement | null) {
        if (!element) return false
        return element.scrollHeight > element.clientHeight
    }

    const checkOverflowState = useCallback(() => {
        setIsOverflowing(isOverflown(reviewTextRef.current))
        if (innerWrapperRef.current) {
            const CARD_WRAPPER_PADDING = 50 // px
            innerWrapperHeight.current =
                (upperContentRef.current?.offsetHeight || 0) +
                (reviewTextRef.current?.scrollHeight || 0) +
                CARD_WRAPPER_PADDING
        }
    }, [])

    useEffect(() => {
        checkOverflowState()

        window.addEventListener('resize', checkOverflowState)
        return () => {
            window.removeEventListener('resize', checkOverflowState)
        }
    }, [checkOverflowState])

    const toggleExpanding = () => {
        setIsExpanded(!isExpanded)
    }

    return (
        <div
            className={classNames(styles.cardWrapper, {
                [styles.overflow]: isOverflowing,
                [styles.shrunk]: !isExpanded,
            })}
            style={{ height: isExpanded ? `${innerWrapperHeight.current}px` : '' }}
            data-testid="card-wrapper"
        >
            <div className={styles.innerWrapper} ref={innerWrapperRef} data-testid="inner-wrapper">
                <div ref={upperContentRef}>
                    <Text as="h3" size="title-sm" weight="extra-extra-bold" altFont={true}>
                        {name}
                    </Text>
                    <Text as="h4" size="sm" altFont={true} className={styles.title}>
                        {title}
                    </Text>
                    <Row>
                        {[...Array(numStars)].map((_, i) => (
                            <MagentaStar key={i} />
                        ))}
                    </Row>
                </div>
                <p className={styles.reviewText} ref={reviewTextRef}>
                    {reviewText}
                </p>
            </div>
            {isOverflowing && (
                <Box className={styles.expandWrapper}>
                    <Button
                        onClick={toggleExpanding}
                        className={styles.expandButton}
                        noFill={true}
                        data-testid="see-more-button"
                    >
                        <Text altFont={true}>{isExpanded ? 'See Less' : 'See More'}</Text>
                    </Button>
                </Box>
            )}
        </div>
    )
}

export default ReviewCard
